import '@digital-realty/ix-chip/ix-chip-set.js';
import '@digital-realty/ix-chip/ix-chip.js';
import '@digital-realty/ix-field/ix-field.js';
import '@digital-realty/ix-icon-button/ix-icon-button.js';
import { mixinDelegatesAria } from '@material/web/internal/aria/delegate.js';
import { mixinElementInternals } from '@material/web/labs/behaviors/element-internals.js';
import { html, LitElement, nothing, PropertyValues } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { IxEmailListStyles } from './ix-email-list-styles.js';
import { isValidEmail } from './utils/email-validation.js';

export interface EmailListItem {
  address: string;
  isValid: boolean;
  name?: string;
  isDuplicate?: boolean;
  isDisconnected?: boolean;
}

const ixEmailListBaseClass = mixinDelegatesAria(
  mixinElementInternals(LitElement)
);

export class IxEmailList extends ixEmailListBaseClass {
  static get styles() {
    return [IxEmailListStyles];
  }

  /** @nocollapse */
  static override shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  /** @nocollapse  */
  static readonly formAssociated = true;

  private readonly internals = (this as HTMLElement) /* needed for closure */
    .attachInternals();

  @query('#email-input') emailInput!: HTMLInputElement;

  // eslint-disable-next-line class-methods-use-this
  @property() lookup: (address?: string) => string | undefined = () =>
    undefined;

  @property({ type: String }) label = '';

  @property({ type: String }) value = '';

  @property({ type: String, attribute: 'error-text' }) errorText =
    'Invalid email in list.';

  @property({ type: Boolean, reflect: true }) disabled = false;

  /**
   * Gets or sets whether or not the text field is in a visually invalid state.
   *
   * This error state overrides the error state controlled by
   * `reportValidity()`.
   */
  @property({ type: Boolean, reflect: true }) error = false;

  @property({ type: Boolean, reflect: true }) required = false;

  // eslint-disable-next-line class-methods-use-this
  @property({ type: Function }) onChanged: any = () => {};

  @property({ type: Boolean, reflect: true }) checkDuplicates = false;

  @property({ type: Number, reflect: true }) maximumEmailCount = -1;

  @property({ type: Number, reflect: true }) tabIndex = 0;

  @property({ type: Number }) maximumEmailLength = -1;

  private errorMessage: string = '';

  /**
   * The associated form element with which this element's value will submit.
   */
  get form() {
    return this.internals.form;
  }

  /**
   * The labels this element is associated with.
   */
  get labels() {
    return this.internals.labels;
  }

  /**
   * The HTML name to use in form submission.
   */
  get name() {
    return this.getAttribute('name') ?? '';
  }

  set name(name: string) {
    this.setAttribute('name', name);
  }

  @state() private focused = false;

  @state() private inputValue = '';

  @state() addresses: EmailListItem[] = [];

  /**
   * Returns the text field's validation error message.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Constraint_validation
   */
  get validationMessage() {
    return this.internals.validationMessage;
  }

  /**
   * Returns a `ValidityState` object that represents the validity states of the
   * text field.
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
   */
  get validity() {
    return this.internals.validity;
  }

  /**
   * Returns whether an element will successfully validate based on forms
   * validation rules and constraints.
   *
   * https://developer.mozilla.org/en-US/docs/Web/API/ElementInternals/willValidate
   */
  get willValidate() {
    return this.internals.willValidate;
  }

  checkValidity() {
    return this.internals.checkValidity();
  }

  reportValidity() {
    return this.internals.reportValidity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.reset(true);
  }

  private chipRemove = async (id: number) => {
    const addresses = this.addresses.filter((_item, i: number) => i !== id);
    this.addresses = [];
    await 0;
    this.addresses = addresses;
    this.isValidList();
    this.value = this.addresses.map(item => item.address).join(',');
    this.onChanged(this.addresses);
    if (!this.addresses.length) {
      this.handleFocusout();
    }
  };

  handleFocusin = () => {
    this.focused = true;
  };

  handleFocusout = () => {
    if (this.inputValue) {
      this.resolveInput();
    }
    this.focused = false;
  };

  keydown = (e: KeyboardEvent) => {
    // input completion keys
    const completionKeys = [' ', ',', 'Tab', 'Enter', ';'];
    if (completionKeys.includes(e.key) && this.inputValue.length) {
      e.preventDefault();
      this.resolveInput();
    }
  };

  resolveInput = () => {
    const address = this.inputValue.toLowerCase();
    const isValid = isValidEmail(address);

    const name = this.lookup(address);
    this.addresses = [...this.addresses, { address, isValid, name }];
    this.inputValue = '';
    this.value = this.addresses.map(item => item.address).join(',');
    this.isValidList();
    this.onChanged(this.addresses);
  };

  input = (e: InputEvent) => {
    const { value } = e.target as HTMLInputElement;
    this.inputValue = value;
  };

  validate() {
    if (this.required && this.addresses.length === 0) {
      this.addError(this.errorText);
    } else {
      this.isValidList();
    }
  }

  clear = (isDisconnected: boolean) => {
    this.addresses = [];
    this.inputValue = '';
    this.value = '';
    this.error = false;
    if (!isDisconnected) {
      this.onChanged(this.addresses);
    }
    this.handleFocusout();
  };

  /** @private */
  formResetCallback() {
    this.reset(false);
  }

  /**
   * Reset the text field to its default value.
   */
  reset(isDisconnected: boolean) {
    this.clear(isDisconnected);
    this.value = this.getAttribute('value') ?? '';
  }

  isValidList = () => {
    this.errorMessage = '';
    this.error = false;

    if (this.addresses.some((item: EmailListItem) => !item.isValid))
      this.addError(this.errorText);

    if (this.checkDuplicates) this.checkForDuplicates();

    if (this.maximumEmailCount > 0) this.validateEmailCount();
    if (this.maximumEmailLength > 0) this.validateEmailLength();

    this.internals.setValidity(
      {
        badInput: this.error,
      },
      this.error ? this.errorMessage : '',
      this.emailInput
    );
  };

  validateEmailCount() {
    if (this.addresses.length > this.maximumEmailCount)
      this.addError(
        `Maximum of ${this.maximumEmailCount} emails are allowed. `
      );
  }

  validateEmailLength() {
    if (
      this.addresses.some(
        (item: EmailListItem) => item.address.length > this.maximumEmailLength
      )
    )
      this.addError(
        `Email address exceeds the maximum length of ${this.maximumEmailLength} characters.`
      );
  }

  checkForDuplicates() {
    let hasDuplicates = false;

    const groupByAddress = this.addresses.reduce((addressMap, email, index) => {
      const { address } = email;

      if (!addressMap[address]) {
        addressMap[address] = [];
      }
      addressMap[address].push(index);
      return addressMap;
    }, {} as Record<string, number[]>);

    Object.values(groupByAddress).forEach((addressIds: number[]) => {
      if (addressIds.length > 1) {
        hasDuplicates = true;
        addressIds.forEach(index => {
          this.addresses[index].isDuplicate = true;
        });
      } else {
        this.addresses[addressIds[0]].isDuplicate = false;
      }
    });

    if (hasDuplicates) {
      this.addError('Duplicate emails detected.');
    }
  }

  static labelText = (item: EmailListItem) => {
    const address = item.isValid ? item.address : `${item.address}!`;
    return item.name ? item.name : address;
  };

  protected override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('disabled')) {
      this.tabIndex = this.disabled ? -1 : 0;
    }
    if (changedProperties.has('value')) {
      this.internals.setFormValue(this.value);
      if (this.value.trim() !== '') {
        this.addresses = this.value.split(',').map((item: string) => {
          const address = item.toLowerCase().trim();
          return {
            address,
            isValid: isValidEmail(address),
          };
        });
      } else {
        this.addresses = [];
      }
      this.isValidList();
    }
  }

  override focus() {
    this.emailInput.focus();
  }

  private addError(errorText: string) {
    this.error = true;
    this.errorMessage += errorText;
  }

  render() {
    const classes = {
      disabled: this.disabled,
      error: !this.disabled && this.error,
    };

    return html`
      <ix-field
        class="${classMap(classes)}"
        ?focused=${this.focused}
        ?disabled=${this.disabled}
        ?required=${this.required}
        ?checkDuplicates=${this.checkDuplicates}
        ?maximumEmailCount=${this.maximumEmailCount}
        label=${this.label}
        ?error=${this.error}
        error-text=${this.errorMessage}
        @focusin=${this.handleFocusin}
        @focusout=${this.handleFocusout}
        ?populated=${this.addresses.length || this.inputValue.length}
      >
        <div class="field-content">
          ${this.addresses.length
            ? html`<ix-chip-set>
                ${this.addresses.map(
                  (item, id: number) => html`<span
                    ><ix-chip
                      @remove=${() => this.chipRemove(id)}
                      label=${IxEmailList.labelText(item)}
                      class=${!item.isValid || item.isDuplicate ? 'error' : ''}
                      appearance="input"
                      removable
                      remove-only
                    ></ix-chip
                  ></span>`
                )}
              </ix-chip-set>`
            : nothing}
          <input
            id="email-input"
            @keydown=${this.keydown}
            @input=${this.input}
            .value=${this.inputValue}
            type="text"
          />
        </div>
        <slot name="end" slot="end">
          ${this.addresses.length
            ? html`<ix-icon-button
                class="clear-icon"
                @click=${() => this.clear(false)}
                icon="close"
                aria-label="clear"
              ></ix-icon-button>`
            : nothing}
        </slot>
      </ix-field>
    `;
  }
}
